import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useMemo, useEffect, useState } from 'react';

import { generateQeId, Col, Heading, Loadable } from '@ctct/ui-react';

import BillingSvcLite from '../../../api/BillingSvcApi';
import { getPlanName } from '../../../utils/orderSummaryUtils';
import { formatCurrency, getCurrencySymbol } from '../../../utils/pricingUtils';
import InfoRow from '../../shared/InfoRow';
import { useCancelFlowContext } from '../CancelFlowContext';

const { getRecurringUsage } = BillingSvcLite;

const OrderSummary = ({ t, qeId, rating, billingInfo, smsTiers }) => {
  const [cancelFlowContext] = useCancelFlowContext();
  const { currencyCode, hasNotRegistered } = cancelFlowContext;
  const {
    monthlyPrice,
    monthlyTaxPrice,
    addOnRating,
    totalTaxPrice,
    ratePlans,
    prepayMonthlyDiscountAmount,
    amountToCharge,
    prepayTotalAmountSaved,
  } = rating || {};
  const [ratePlan] = ratePlans || [{}];
  const addOnsMonthlyCost = addOnRating
    ? addOnRating.reduce((accumulator, addOn) => accumulator + addOn.monthlyCost, 0)
    : 0;
  const addOnsMonthlyTax = addOnRating
    ? addOnRating.reduce((accumulator, addOn) => accumulator + addOn.monthlyTax, 0)
    : 0;
  const addOnsMonthlyDiscount = addOnRating
    ? addOnRating.reduce((accumulator, addOn) => accumulator + addOn.monthlyDiscount, 0)
    : 0;
  const currencySymbol = getCurrencySymbol(currencyCode);
  const estimatedNextInvoice = monthlyPrice + monthlyTaxPrice + addOnsMonthlyCost + addOnsMonthlyTax;
  const integerPartPrice = (amountToCharge && Math.trunc(amountToCharge).toLocaleString()) || '0';
  const [{ period }] = addOnRating || [{}];
  let planDescription = '';
  const [isLoading, setIsLoading] = useState(true);
  const [nextBillDate, setNextBillDate] = useState('');
  const [contactTierLabel, setContactTierLabel] = useState('');
  const [smsTier, setSmsTier] = useState('');
  const [sms, setSms] = useState();
  const [inboxPreview, setInboxPreview] = useState();

  const formatMoney = (amount) => {
    return amount == null || Number.isNaN(amount) || !currencyCode ? '' : formatCurrency(currencyCode, amount, 2);
  };
  const defaultValue = formatMoney(0);

  const prepayDiscountPercentage = useMemo(() => {
    return period > 1 ? (prepayMonthlyDiscountAmount / (monthlyPrice + prepayMonthlyDiscountAmount)) * 100 : 0;
  }, [period, monthlyPrice, prepayTotalAmountSaved]);

  const decimalPrice = useMemo(() => {
    let retVal = '00';

    if (amountToCharge != null) {
      retVal = new Intl.NumberFormat({
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format((amountToCharge * 100) % 100);

      if (retVal.length === 1) {
        retVal = `0${retVal}`;
      }
    }

    return retVal;
  }, [amountToCharge]);

  useEffect(() => {
    if (!billingInfo) return;

    const {
      overview: {
        next_charge_scheduled: nextChargeDate,
        contact_tier: contactTier,
        days_left_in_period: daysLeftInPeriod,
      },
    } = billingInfo;
    const nextBillingDate = new Date(nextChargeDate);

    nextBillingDate.setDate(nextBillingDate.getDate() - 1);

    const dateOptions = { month: 'long', day: 'numeric' };

    if (nextBillingDate.getFullYear() !== new Date().getFullYear()) {
      dateOptions.year = 'numeric';
    }

    const displayDate = nextBillingDate.toLocaleString('default', dateOptions);
    const currentDate = new Date();

    currentDate.setDate(currentDate.getDate() + daysLeftInPeriod);
    setNextBillDate(displayDate);
    setContactTierLabel(contactTier);
  }, [billingInfo]);

  useEffect(() => {
    if (!currencyCode || !ratePlan.planId || (!hasNotRegistered && !smsTiers)) return;

    (async () => {
      try {
        const {
          data: { minValue },
        } = await getRecurringUsage();
        const smsAddOn = addOnRating.find((ao) => ao.id === 'CTCT-SMS-ADDON');

        setSms(smsAddOn);

        const inbox = addOnRating.find((ao) => ao.id === 'CTCT-INBOX-PREVIEW-101');

        setInboxPreview(inbox);

        const tier = smsTiers && smsTiers.find((ti) => ti.tierMin === minValue);
        // eslint-disable-next-line
        setSmsTier(
          `${tier.tierMin} - ${
            tier.tierMax > 0
              ? tier.tierMax
              : t('cancel-flow.steps.downgrade-cancel.downgrade.compare.next-invoice.unlimited')
          }`
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [currencyCode, ratePlan, smsTiers]);

  if (ratePlan && ratePlan.planTypeItem && ratePlan.planTypeItem.description) {
    const {
      planTypeItem: { description },
    } = ratePlan;

    planDescription = description;
  }

  return (
    <Col widths={['lg']} lg="3" className="next-invoice-compare">
      <Loadable loading={isLoading}>
        <Heading level={5} text={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.next-invoice.title')} />
        <div className="order-summary-section-container" data-qe-id={generateQeId(qeId, 'contacts-plan')}>
          <InfoRow
            data-qe-id={generateQeId(qeId, 'plan')}
            label={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.next-invoice.plan', {
              plan: getPlanName({ planName: planDescription, header: false, onlyText: true }),
              tier: contactTierLabel,
            })}
            value={formatMoney(monthlyPrice + prepayMonthlyDiscountAmount) || defaultValue}
          />
        </div>

        <div className="order-summary-section-container" data-qe-id={generateQeId(qeId, 'add-ons')}>
          {sms && (
            <InfoRow
              data-qe-id={generateQeId(qeId, 'sms')}
              label={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.next-invoice.sms', {
                tier: smsTier,
              })}
              value={(sms && formatMoney(sms.listPrice)) || defaultValue}
            />
          )}

          {inboxPreview && (
            <InfoRow
              data-qe-id={generateQeId(qeId, 'inbox-preview')}
              label={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.next-invoice.inbox-preview')}
              value={(inboxPreview && formatMoney(inboxPreview.listPrice)) || defaultValue}
            />
          )}
        </div>

        {prepayMonthlyDiscountAmount > 0 && (
          <div
            className="order-summary-section-container order-summary-discount"
            data-qe-id={generateQeId(qeId, 'discount')}
          >
            <InfoRow
              data-qe-id={generateQeId(qeId, 'plan')}
              label={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.next-invoice.discount', {
                percentage: prepayDiscountPercentage,
              })}
              value={`-${formatMoney(prepayMonthlyDiscountAmount + addOnsMonthlyDiscount)}`}
            />
          </div>
        )}

        {totalTaxPrice > 0 && (
          <div
            className="order-summary-section-container order-summary-taxes-balance"
            data-qe-id={generateQeId(qeId, 'taxes-balance')}
          >
            {totalTaxPrice > 0 && (
              <InfoRow
                data-qe-id={generateQeId(qeId, 'sms')}
                label={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.next-invoice.taxes')}
                value={formatMoney(totalTaxPrice) || defaultValue}
              />
            )}
          </div>
        )}

        <div className="order-summary-section-container" data-qe-id={generateQeId(qeId, 'total')}>
          <div className={classNames('order-summary-section-row', 'order-summary-total-row')}>
            <span
              className={classNames('order-summary-total-label', 'order-summary-body-left')}
              data-qe-id={generateQeId(qeId, 'total')}
            >
              {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.next-invoice.todays-total')}
            </span>
            <div
              className="order-summary-body-right total-value-wrapper"
              data-qe-id={generateQeId(qeId, 'total-value')}
              style={rating ? {} : { visibility: 'hidden' }}
            >
              <span className="total-value-currency">{currencySymbol}</span>
              <span className="total-value-integer">{integerPartPrice}</span>
              <span className="total-value-decimal">{decimalPrice}</span>
            </div>
          </div>

          <InfoRow
            data-qe-id={generateQeId(qeId, 'next-month')}
            label={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.next-invoice.next-month-price')}
            value={formatMoney(estimatedNextInvoice) || defaultValue}
          />

          <InfoRow
            data-qe-id={generateQeId(qeId, 'next-billing-date')}
            label={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.next-invoice.next-billing-date')}
            value={nextBillDate}
          />
        </div>
      </Loadable>
    </Col>
  );
};

OrderSummary.propTypes = {
  t: PropTypes.func.isRequired,
  qeId: PropTypes.string.isRequired,
  rating: PropTypes.oneOfType([PropTypes.object]),
  billingInfo: PropTypes.oneOfType([PropTypes.object]),
  smsTiers: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
};

OrderSummary.defaultProps = {
  rating: null,
  billingInfo: null,
  smsTiers: null,
};

export default OrderSummary;
